import { useState, useRef, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  User,
  TrendingUp,
  Inbox,
  Search,
  CarFront,
  Lock,
  LogOut,
  LayoutDashboard,
  Map as MapIcon,
  BellRing,
  Sprout,
  ShieldAlert,
  Menu,
  X,
  RotateCw,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ZippelMark, ZippelWordmark } from './ZippelBrand';
// Named import: Vite bundles just this field, not the whole package.json.
import { version } from '../../package.json';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  Icon: typeof Home;
}

const MAIN_NAV: NavItem[] = [
  { path: '/dashboard', label: 'Home', Icon: Home },
  { path: '/staff-users', label: 'Staff Users', Icon: Users },
  { path: '/users', label: 'People', Icon: User },
  { path: '/activity', label: 'Activity', Icon: TrendingUp },
  { path: '/reports', label: 'Inbox', Icon: Inbox },
  { path: '/rides', label: 'Rides', Icon: CarFront },
  { path: '/search-history', label: 'Search history', Icon: Search },
];

const STATISTICS_NAV: NavItem[] = [
  { path: '/statistics/overview', label: 'Overview', Icon: LayoutDashboard },
  { path: '/statistics/marketplace', label: 'Marketplace', Icon: MapIcon },
  { path: '/statistics/captures', label: 'Ride Captures', Icon: BellRing },
  { path: '/statistics/growth', label: 'Growth', Icon: Sprout },
  { path: '/statistics/trust', label: 'Trust & Safety', Icon: ShieldAlert },
];

/** Width of one page-background tile; never upscaled (see PageBackground). */
const BG_TILE = 720;

/**
 * The users app's topographic page background. The tile isn't horizontally
 * seamless, so the width is split into tile-sized columns and every other one
 * is mirrored, which makes each join continuous (as in the app's
 * PageBackground.web.tsx).
 */
const PageBackground = () => {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const columns = Math.max(1, Math.ceil(width / BG_TILE));
  return (
    <div className="page-bg" aria-hidden="true">
      {Array.from({ length: columns }).map((_, i) => (
        <div key={i} className={`page-bg-column${i % 2 === 1 ? ' mirrored' : ''}`} />
      ))}
    </div>
  );
};

/**
 * The staff portal frame, matching the users app: a cream header with the
 * Zippel mark and wordmark over a teal strip, the topographic page background,
 * and the navigation in a sidebar that becomes a slide-out drawer on narrow
 * screens.
 */
const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;

  const initials = () => {
    const name: string = user?.fullName || '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (user?.email?.[0] || 'U').toUpperCase();
  };

  // Close the user menu when clicking outside it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The drawer closes on navigation and on Escape.
  useEffect(() => setDrawerOpen(false), [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setDrawerOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const go = (path: string, keepStatisticsFilters = false) =>
    navigate(`${path}${keepStatisticsFilters && location.pathname.startsWith('/statistics/') ? location.search : ''}`);

  const navLink = ({ path, label, Icon }: NavItem, keepStatisticsFilters = false) => (
    <button
      key={path}
      type="button"
      onClick={() => go(path, keepStatisticsFilters)}
      className={`sidebar-link ${isActive(path) ? 'active' : ''}`}
      aria-current={isActive(path) ? 'page' : undefined}
    >
      <span className="sidebar-icon">
        <Icon size={20} />
      </span>
      <span className="sidebar-label">{label}</span>
    </button>
  );

  return (
    <div className="layout">
      <PageBackground />

      <header className="app-header">
        <div className="app-header-bar">
          <div className="app-header-side">
            <button
              type="button"
              className="header-icon-button drawer-toggle"
              onClick={() => setDrawerOpen((open) => !open)}
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={drawerOpen}
            >
              {drawerOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>

          <button type="button" className="app-brand" onClick={() => navigate('/dashboard')} aria-label="Zippel Staff home">
            <ZippelMark size={34} />
            <ZippelWordmark height={26} />
            <span className="app-brand-tag">Staff</span>
          </button>

          <div className="app-header-side app-header-side-end">
            <button
              type="button"
              className="header-icon-button"
              onClick={() => window.location.reload()}
              title="Refresh page"
              aria-label="Refresh page"
            >
              <RotateCw size={20} />
            </button>

            <div className="header-user" ref={dropdownRef}>
              <button
                type="button"
                className="user-button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
              >
                <span className="user-avatar">{initials()}</span>
                <span className="user-name">{user?.fullName || user?.email}</span>
                <ChevronDown size={16} className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="user-dropdown" role="menu">
                  <button
                    type="button"
                    className="dropdown-item"
                    role="menuitem"
                    onClick={() => {
                      setDropdownOpen(false);
                      navigate('/change-password');
                    }}
                  >
                    <Lock size={16} />
                    <span>Change Password</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button
                    type="button"
                    className="dropdown-item logout"
                    role="menuitem"
                    onClick={() => {
                      setDropdownOpen(false);
                      logout();
                      navigate('/login');
                    }}
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="app-header-strip" />
      </header>

      <div className="layout-body">
        <aside className={`sidebar ${drawerOpen ? 'open' : ''}`} aria-label="Main navigation">
          <nav className="sidebar-nav">
            {MAIN_NAV.map((item) => navLink(item))}
            <div className="sidebar-section-title">Statistics</div>
            {/* Carry the period and campus over between statistics screens. */}
            {STATISTICS_NAV.map((item) => navLink(item, true))}
          </nav>
          <div className="sidebar-version">Version {version}</div>
        </aside>
        {drawerOpen && <div className="sidebar-overlay" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}

        <main className="main-content">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
